import {
  useMutation as useApolloMutation,
  useLazyQuery as useApolloLazyQuery,
  useQuery as useApolloQuery,
} from '@apollo/react-hooks';
import { useLocale } from '@redlink/assets/locale';
import { useAlert } from '@redlink/components/molecules/useMessage';

export const useMutation = (query, { done, failed, limited, ...rest }) => {
  const { getEx } = useExceptionDetail();
  const alert = useAlert();
  const [action, other] = useApolloMutation(query, {
    onCompleted: done,
    onError: (err) => {
      const errorDetail = getEx(err);
      failed && failed(errorDetail);

      // plan limitation exception
      if (errorDetail.code !== 28)
        alert.show(errorDetail.message, { type: 'error' });
      else limited && limited(errorDetail.args);
    },
    ...rest,
  });

  return [(input) => action({ variables: input }), other];
};

export const useQuery = (query, { done, failed, limited, ...rest } = {}) => {
  const { getEx } = useExceptionDetail();
  const {
    refetch: action,
    data = {},
    ...other
  } = useApolloQuery(query, {
    onCompleted: done,
    onError: (err) => {
      const errorDetail = getEx(err);
      failed && failed(errorDetail);

      // plan limitation exception
      if (errorDetail.code === 28) limited && limited(errorDetail.args);
    },
    ...rest,
  });

  return {
    refetch: (input) => action({ variables: input }),
    data,
    ...other,
  };
};

export const useLazyQuery = (query, { done, failed, ...rest } = {}) => {
  const { getEx } = useExceptionDetail();
  const [action, other] = useApolloLazyQuery(query, {
    onCompleted: done,
    onError: (err) => {
      const errorDetail = getEx(err);
      failed && failed(errorDetail);
    },
    ...rest,
  });

  return [(input) => action({ variables: input }), other];
};

const useExceptionDetail = () => {
  const { dictionary = {} } = useLocale();
  return {
    getEx: ({ graphQLErrors }) => {
      const { extensions: { code = 0, ...args } = {} } = graphQLErrors[0] || {};
      const message = dictionary[`exception${code}`];
      return {
        code: parseInt(code),
        message,
        args,
      };
    },
  };
};
